import React, { useState, useEffect } from "react";
import "./App.css";
import { AppProvider, useApp } from "./context/AppContext";
import LoginScreen from "./components/LoginScreen";
import RegisterScreen from "./components/RegisterScreenNew";
import ResponsiveMainScreen from "./components/ResponsiveMainScreen";
import CallingScreen from "./components/CallingScreen";
import IncomingScreen from "./components/IncomingScreen";
import InCallScreen from "./components/InCallScreen";
import MainProvider from "./components/MainProvider";
import PaymentSuccess from "./components/PaymentSuccess";
import PaymentCancel from "./components/PaymentCancel";
import PaymentHistory from "./components/PaymentHistory";

const AppContent: React.FC = () => {
  const [showRegister, setShowRegister] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const { state, cancelCall, acceptCall, rejectCall, hangUp } = useApp();

  // Listen for popstate events to enable client-side navigation
  useEffect(() => {
    const handlePopState = () => {
      console.log("[App.tsx] popstate event - new path:", window.location.pathname);
      setCurrentPath(window.location.pathname);
    };

    window.addEventListener('popstate', handlePopState);
    console.log("[App.tsx] Added popstate listener");

    return () => {
      window.removeEventListener('popstate', handlePopState);
      console.log("[App.tsx] Removed popstate listener");
    };
  }, []);

  // Use currentPath for routing instead of reading window.location.pathname once
  const path = currentPath;

  // Check if there is a valid token and wait for session restore
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setIsLoading(false);
    } else if (state.currentUserId && state.user) {
      // Session restored successfully
      setIsLoading(false);
    }
  }, [state.currentUserId, state.user]);

  // Show payment success/cancel pages (these don't require login)
  if (path === "/payment/success") {
    return <PaymentSuccess />;
  }
  if (path === "/payment/cancel") {
    return <PaymentCancel />;
  }

  // Show payment history page (requires login)
  if (path === "/payment/history") {
    const token = localStorage.getItem("token");

    // Show loading while session is being restored
    if (token && !state.currentUserId) {
      return (
        <div className="screen">
          <div className="loading">
            <h2>Loading...</h2>
          </div>
        </div>
      );
    }

    // Redirect to login if not authenticated
    if (!state.currentUserId || !state.user) {
      window.location.href = "/";
      return null;
    }

    return <PaymentHistory />;
  }

  // Show login/register screen when user is not logged in
  if (!state.currentUserId || !state.user) {
    if (showRegister) {
      return (
        <RegisterScreen
          onRegisterSuccess={() => setShowRegister(false)}
          onSwitchToLogin={() => setShowRegister(false)}
        />
      );
    } else {
      return <LoginScreen onSwitchToRegister={() => setShowRegister(true)} />;
    }
  }

  // User is logged in, show appropriate screen based on current state
  const renderScreen = () => {
    // console.log("Current state:", state);
    // console.log("Current user:", state.user);
    // console.log("Current screen:", state.screen);

    if (!state.user) {
      return <LoginScreen onSwitchToRegister={() => setShowRegister(true)} />;
    }

    switch (state.screen) {
      case "main":
        return <ResponsiveMainScreen />;
      case "main-provider":
        return <MainProvider />;
      case "calling":
        return (
          <CallingScreen
            targetUserId={state.targetUserId || ""}
            conversationId={state.conversationId || ""}
            onCancel={cancelCall}
          />
        );
      case "incoming":
        return (
          <IncomingScreen
            callerId={state.callerId || ""}
            conversationId={state.conversationId || ""}
            onAccept={acceptCall}
            onReject={rejectCall}
          />
        );
      case "in-call":
        return (
          <InCallScreen
            userId={state.targetUserId || state.callerId || ""}
            conversationId={state.conversationId || ""}
            onHangUp={hangUp}
          />
        );
      default:
        // Return default page based on user role
        const component =
          state.user.role === "normal" ? <ResponsiveMainScreen /> : <MainProvider />;
        return component;
    }
  };

  return (
    <div className="App">
      {/* <h1>VocreaAI Chat</h1> */}
      {renderScreen()}
    </div>
  );
};

const App: React.FC = () => {
  return (
    <AppProvider>
      <AppContent />
    </AppProvider>
  );
};

export default App;
